/**
 * journeyConsent.ts — the in-questionnaire marketing consent: the shape of the
 * BROWSER-HELD ticks, the ONE read that decides whether WhatsApp is offered, and
 * the ONE post-submit grant write.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: marketing consent
 * one-flow (2026-09-23, CTO: the Result screen's "Keeping in touch" card cost
 * two extra clicks — the boxes now sit on the questionnaire's consent screen so
 * the whole thing is done in one go).
 *
 * WHY THE TICKS ARE BROWSER STATE. clinic-service accepts the marketing-consent
 * WRITE only AFTER a submit with an eligible/clinician_review verdict — on a
 * draft it 404s. The consent screen renders BEFORE submit, so the tick is held
 * in memory (JourneyPage state, `MarketingChoices`) and recorded by
 * `postJourneyMarketingGrant` the moment the submit has SUCCEEDED. Both boxes
 * render UNTICKED (the lawful default, PECR reg. 22 / UK GDPR Art 7) and a write
 * may carry a tick and NEVER a `false`: an unticked box is a default, not a
 * withdrawal (`consentChannels.ts` owns that rule; this module calls it with
 * `stored: null`).
 *
 * WHY WHATSAPP IS OFFERED ONLY WHEN THE DRAFT READ SAYS SO (`useWhatsappOffer`).
 * The approved WhatsApp wording (`mc-2026-09-11-d1`, unchanged) says "on the
 * mobile number I gave you", so the box may only be shown to someone who holds a
 * UK mobile — and that rule lives SERVER-side and is not reproduced in this
 * bundle. The first cut of the one-flow move therefore showed EMAIL alone, which
 * a review found was a real regression: the old Result-screen card offered
 * WhatsApp to everyone with a UK mobile. clinic-service (`fix/consent-step-source`)
 * now answers the marketing-consent READ for an in-progress draft too, so the
 * block reads it ONCE when the q15 screen mounts and adds the WhatsApp box when,
 * and only when, `whatsappAvailable === true`. While the read is in flight, and
 * whenever it fails, 404s (an older clinic-service — the deploy window) or hangs,
 * the block shows EMAIL alone and says nothing: unknown means not offered, which
 * is exactly the face the old card wore after a failed read.
 *
 * ⚠️ THE READ SUPPLIES ONE BOOLEAN AND NOTHING ELSE. The stored `email` /
 * `whatsapp` it also carries are dropped at the hook's boundary, so they cannot
 * reach a box: a pre-ticked box is not valid consent (PECR), and this lane is
 * grants-only, so an existing grant is simply left as it is. That is also why the
 * write still calls `channelsToSend` with `stored: null` even though a read now
 * exists — using the read as the prior state would turn an unticked box over a
 * stored grant into a `false`, a withdrawal nobody made.
 *
 * THE READ NEVER BLOCKS. Nothing waits on it: the email box and Continue work
 * from the first paint, the block carries no busy state, and there is no error
 * copy for it anywhere. It is bounded by the same settle budget as the write, so
 * an answer that arrives after that is ignored — a box must not appear under the
 * patient's finger long after they have started acting on the screen.
 *
 * THE WRITE NEVER BLOCKS. It fires AFTER the submit result is in hand, is not
 * awaited by anything on the patient's path, never rejects, and settles within
 * `MARKETING_CONSENT_TIMEOUT_MS` regardless. Every refusal is swallowed on
 * purpose: a 404/409 from a `not_eligible` verdict is the server correctly
 * refusing to record marketing consent for a journey that ends at the stop
 * screen (parity with the old placement, where only the eligible Result screen
 * ever captured it), and a 5xx/timeout loses AT WORST a grant — we fail to
 * contact someone who said yes, never the reverse. No error is shown: the
 * patient has moved on to their result, and a failure notice about an optional
 * box would be noise over the verdict they came for.
 */
import { useEffect, useState } from 'react';
import { getMarketingConsent, saveMarketingConsent } from '../api/journey';
import type { MarketingChannel } from '../api/types';
import { channelsToSend } from './consentChannels';
import { MARKETING_CONSENT_WORDING_VERSION } from './consentWording';
import type { MarketingChoices } from './MarketingConsentFields';

/**
 * The lane's settle budget — for the write, and for how long the WhatsApp offer
 * check is listened to. Nothing on the patient path waits on either (the grant
 * is fire-and-forget since the one-flow move, and the read never holds anything
 * up), but both stay bounded: a hung write must still settle so a spec — or any
 * future caller — can await the outcome without inheriting an open handle, and
 * a read that answers after this is ignored rather than dropping a new box onto
 * a screen the patient is already using.
 */
export const MARKETING_CONSENT_TIMEOUT_MS = 4000;

/** What happened to the grant write. Informational only — no caller may branch
 * the patient's journey on it. */
export type ConsentCommitOutcome = 'saved' | 'failed' | 'timed_out';

/**
 * The questionnaire consent screen's handle on the ticks: the current choices,
 * the toggle, and the report of whether the WhatsApp box is on screen. State
 * lives in JourneyPage (it must outlive QuestionFlow remounts — a hard-stop
 * "Back" re-mints the assessment and replays answers, and the tick the patient
 * already made goes with them).
 */
export interface JourneyMarketingTicks {
  choices: MarketingChoices;
  onToggle: (channel: MarketingChannel) => void;
  /**
   * Told, by the block itself, whether the WhatsApp box is RENDERED: `false`
   * when the block mounts (the offer check has not answered), `true` only once
   * the box is actually on screen. The grant reads the last report, so it can
   * name WhatsApp only when the patient was shown the box they ticked.
   *
   * REQUIRED, not optional, on purpose: a caller that rendered the box but
   * never heard about it would silently drop every WhatsApp tick — the patient
   * sees a box, ticks it, and nothing is recorded. Making the report part of
   * the handle's type turns that into a compile error.
   */
  onWhatsappShownChange: (shown: boolean) => void;
}

/**
 * The WhatsApp OFFER CHECK for the q15 "Keeping in touch" block: read the
 * assessment's marketing-consent state ONCE, when the calling screen mounts, and
 * answer whether the WhatsApp box may be shown. `true` only when the server says
 * `whatsappAvailable === true` (the lead holds a UK mobile) within
 * `MARKETING_CONSENT_TIMEOUT_MS`; `false` while loading, on any failure, on a
 * 404 (a clinic-service that predates the draft read, or any "not for you"
 * case), on a hang, and on an answer that arrives after the budget.
 *
 * It returns a BOOLEAN, and that is the point: the stored `email`/`whatsapp`
 * never leave this function, so no caller can pre-tick a box from them.
 * Failures are swallowed here, so no caller can paint an error for them either.
 *
 * "Once" per mount and per assessment. React's dev-only StrictMode double
 * effect issues a second GET in development; that is a harmless repeat of a
 * read, and a started-flag to suppress it would cancel the only listener that
 * could ever answer (the first effect's cleanup runs before the second effect).
 */
export function useWhatsappOffer(assessmentId: string, clinicSlug: string): boolean {
  // Keyed on the assessment it was answered for, so a changed id can never
  // inherit the previous answer — and so there is no synchronous reset to do.
  const key = `${clinicSlug}/${assessmentId}`;
  const [offeredFor, setOfferedFor] = useState<string | null>(null);

  useEffect(() => {
    let listening = true;
    const stopListening = setTimeout(() => {
      listening = false;
    }, MARKETING_CONSENT_TIMEOUT_MS);
    getMarketingConsent(assessmentId, clinicSlug).then(
      (state) => {
        // `=== true`, never truthy: a body this build does not understand is
        // "unknown", and unknown never offers the box.
        if (listening && (state as Partial<{ whatsappAvailable: unknown }> | undefined)?.whatsappAvailable === true) {
          setOfferedFor(key);
        }
      },
      // Every failure is the same "unknown": no box, no error, no retry.
      () => undefined,
    );
    return () => {
      listening = false;
      clearTimeout(stopListening);
    };
  }, [assessmentId, clinicSlug, key]);

  return offeredFor === key;
}

/**
 * Resolve with the write's outcome, or with `timed_out` after `ms` — whichever
 * comes first. Never rejects: a failed write is an outcome, not an exception.
 */
function settleWithin(work: Promise<unknown>, ms: number): Promise<ConsentCommitOutcome> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve('timed_out'), ms);
    work.then(
      () => {
        clearTimeout(timer);
        resolve('saved');
      },
      () => {
        clearTimeout(timer);
        resolve('failed');
      },
    );
  });
}

/**
 * Record the ticks the patient made on the consent screen, right after a
 * SUCCESSFUL submit. `null` = nothing to record (nothing ticked that was shown)
 * — no request is made at all. Otherwise a promise that ALWAYS RESOLVES within
 * `MARKETING_CONSENT_TIMEOUT_MS`; the caller fires it `void` and continues.
 *
 * `shown.whatsapp` is whether the WhatsApp box was RENDERED on the consent
 * screen (the block's own report — `JourneyMarketingTicks.onWhatsappShownChange`).
 * Email is always on screen when the block is. A WhatsApp tick is sent only
 * when the box was shown: a box the patient never saw cannot carry their choice.
 *
 * The body is built by `channelsToSend` with `stored: null` — the offer check
 * reads the server's state but its stored values are deliberately never used
 * as a prior state (see the module header): only channels the screen SHOWED,
 * only as `true`, never a `false`. An untouched box is a default, and turning it
 * into a withdrawal would be a false entry in the one record that has to
 * demonstrate consent (UK GDPR Art 7(1)).
 */
export function postJourneyMarketingGrant(
  assessmentId: string,
  clinicSlug: string,
  ticks: MarketingChoices,
  shown: { whatsapp: boolean },
): Promise<ConsentCommitOutcome> | null {
  const channels = channelsToSend(ticks, { email: true, whatsapp: shown.whatsapp }, null);
  if (!channels) return null;
  return settleWithin(
    saveMarketingConsent(assessmentId, {
      clinicSlug,
      ...channels,
      wordingVersion: MARKETING_CONSENT_WORDING_VERSION,
    }),
    MARKETING_CONSENT_TIMEOUT_MS,
  );
}
